// One-time hints (founder, build 44), per account, per device (localStorage).
// Storage that can't be read = no hint, never an error.
//   price    the bubble under the Request Price box on Requests (pick A): due
//            from the end of the practice cards until the first tap on the
//            box. Talent who finished the practice before build 44 never see
//            it.
//   preview  "Preview my public page" at the top of the Profile tab hops until
//            the first preview (founder: "they just see it the first time they
//            open the Profile tab") — every talent, the practice or not.
//   spotlight  the small "New" on the QR tab's Spotlight button (founder,
//            1.5.2 b50) until the first tap on it.
//   verify   "Verify your Instagram" opening by itself once the bank is done
//            (founder, 2026-11-03; hooks/useVerifyInstagramNudge).
import { takesMoney } from "../utils/takesMoney";
import { instagramIsNext, whichGate } from "./connectBank";

export type WelcomeHint = "price" | "preview" | "spotlight" | "verify";

// price: "1" while due. preview / spotlight / verify: "1" once seen.
const PRICE_KEY = (slug: string) => `rq_price_coach_${slug}`;
const SEEN_KEY: Record<Exclude<WelcomeHint, "price">, (slug: string) => string> = {
  preview: (slug) => `rq_preview_seen_${slug}`,
  spotlight: (slug) => `rq_spotlight_seen_${slug}`,
  verify: (slug) => `rq_verify_opened_${slug}`,
};

/** What the price bubble says. price = "tap to change it"; bank = connect your bank first; review = paid requests start after approval. */
export type PriceHintKind = "price" | "bank" | "review";

type HintProfile = {
  canTakeMoney?: boolean;
  approvalStatus?: string;
  isStripeOnboarded?: boolean;
  isAdminCreated?: boolean;
  chosenDeferredStripe?: boolean;
} | null | undefined;

/** What the price bubble says to this talent. null = nothing: a declined applicant has no price to change, and "after approval" would read as "just wait" to a talent who still has to verify Instagram (the banner says so instead). */
export function priceHintKind(profile: HintProfile): PriceHintKind | null {
  if (!profile) return null;
  if (profile.approvalStatus === "declined") return null;
  if (takesMoney(profile)) return "price";
  if (instagramIsNext(profile)) return null;
  return whichGate(profile) === "bank" ? "bank" : "review";
}

export function hintDue(hint: WelcomeHint, slug?: string | null): boolean {
  if (!slug) return false;
  try {
    return hint === "price"
      ? localStorage.getItem(PRICE_KEY(slug)) === "1"
      : localStorage.getItem(SEEN_KEY[hint](slug)) !== "1";
  } catch {
    return false;
  }
}

/** The practice is done on this device: the price bubble is due. */
export function startPriceHint(slug: string): void {
  try {
    localStorage.setItem(PRICE_KEY(slug), "1");
  } catch {
    /* storage unavailable — no bubble, nothing else changes */
  }
}

/** The first tap: that hint is gone for good. */
export function endHint(hint: WelcomeHint, slug: string): void {
  try {
    if (hint === "price") localStorage.removeItem(PRICE_KEY(slug));
    else localStorage.setItem(SEEN_KEY[hint](slug), "1");
  } catch {
    /* storage unavailable — nothing to remember */
  }
}
